import React, { useEffect, useMemo, useState } from 'react';
import { ShoppingBag, X, ChevronRight } from 'lucide-react';
import { APR_PRODUCTS, APR_STORE, aprUrl, matchAprProducts } from '../../data/aprProducts';

const LINK_RE = /americanpeptideresearch\.net\/product\/([a-z0-9-]+)\/ref\/16/gi;

function stackSlugs(article) {
  const found = [];
  let m;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(article?.content || '')) !== null) {
    const s = m[1].toLowerCase();
    if (APR_PRODUCTS[s] && !found.includes(s)) found.push(s);
  }
  if (found.length) return found.slice(0, 8);
  return matchAprProducts(`${article?.title || ''} ${(article?.tags || []).join(' ')}`, 6);
}

const ItemLink = ({ slug, compact }) => {
  const p = APR_PRODUCTS[slug];
  return (
    <a
      href={aprUrl(slug)}
      target="_blank"
      rel="noopener sponsored"
      data-apr-product={slug}
      data-apr-name={p.name}
      data-testid={`apr-rail-item-${slug}`}
      className={`group flex items-center gap-3 bg-zinc-900/80 border border-zinc-800 hover:border-[#c8ff00]/60 transition-colors rounded-lg p-2 ${compact ? 'min-w-[210px]' : ''}`}
    >
      <img src={`/apr/${slug}.webp`} alt={p.name} width={40} height={40} loading="lazy"
        className="w-10 h-10 object-contain bg-white rounded-md p-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-zinc-200 text-xs font-semibold truncate">{p.name}</p>
        <p className="text-[#c8ff00] text-xs font-bold">${p.price}</p>
      </div>
      <ChevronRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-[#c8ff00] shrink-0 transition-colors" />
    </a>
  );
};

/** Sticky "Shop this stack" rail — APR vials + prices for the peptides covered in the article. */
export default function AprStickyRail({ article }) {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const slugs = useMemo(() => stackSlugs(article), [article]);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!slugs.length || !visible) return null;

  return (
    <>
      {/* Desktop: right-edge rail */}
      <div className="hidden xl:block fixed right-3 top-1/2 -translate-y-1/2 z-40" data-testid="apr-sticky-rail">
        {open ? (
          <div className="w-64 bg-zinc-950/95 backdrop-blur-md border border-zinc-800 rounded-xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-zinc-800">
              <span className="text-[#c8ff00] text-xs font-bold tracking-widest uppercase flex items-center gap-1.5">
                <ShoppingBag className="w-3.5 h-3.5" /> Shop This Stack
              </span>
              <button onClick={() => setOpen(false)} data-testid="apr-rail-collapse" aria-label="Collapse shop rail"
                className="text-zinc-500 hover:text-white transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="p-2 space-y-1.5 max-h-[55vh] overflow-y-auto">
              {slugs.map((s) => <ItemLink key={s} slug={s} />)}
            </div>
            <a href={APR_STORE} target="_blank" rel="noopener sponsored" data-apr-name="APR Store"
              data-testid="apr-rail-store-link"
              className="block text-center text-[11px] text-zinc-400 hover:text-[#c8ff00] py-2 border-t border-zinc-800 transition-colors">
              All APR peptides — discount auto-applied →
            </a>
          </div>
        ) : (
          <button onClick={() => setOpen(true)} data-testid="apr-rail-toggle"
            className="bg-[#c8ff00] text-black text-xs font-bold tracking-widest uppercase px-2 py-4 rounded-l-lg shadow-lg hover:opacity-90 transition-opacity"
            style={{ writingMode: 'vertical-rl' }}>
            Shop This Stack
          </button>
        )}
      </div>

      {/* Mobile: bottom-left pill + horizontal sheet */}
      <div className="xl:hidden" data-testid="apr-mobile-rail">
        {sheetOpen ? (
          <div className="fixed inset-x-0 bottom-0 z-40 bg-zinc-950/97 backdrop-blur-md border-t border-zinc-800 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#c8ff00] text-xs font-bold tracking-widest uppercase flex items-center gap-1.5">
                <ShoppingBag className="w-3.5 h-3.5" /> Shop This Stack
              </span>
              <button onClick={() => setSheetOpen(false)} data-testid="apr-sheet-close" aria-label="Close shop sheet"
                className="text-zinc-400 hover:text-white p-1"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {slugs.map((s) => <ItemLink key={s} slug={s} compact />)}
            </div>
          </div>
        ) : (
          <button onClick={() => setSheetOpen(true)} data-testid="apr-mobile-pill"
            className="fixed bottom-4 left-4 z-40 bg-[#c8ff00] text-black text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 hover:opacity-90 transition-opacity">
            <ShoppingBag className="w-3.5 h-3.5" /> Shop this stack · {slugs.length}
          </button>
        )}
      </div>
    </>
  );
}
